import { Table } from 'vue'
import { ActionTree, GetterTree, Module, MutationTree } from 'vuex'
import { State } from '../types'
import {
    countGoodRoadTables,
    filterGoodRoadGameTables,
    isGoodRoadGameType,
    splitGoodRoadTablesByType
} from '@/components/desk/good-road/utils'
import { Token } from '@/utils/constants'
import { getCookieValue, setCookieValue } from '@/utils'

export type DesksByType = {
    G002: Table[]
    G003: Table[]
}

export interface GoodRoadState {
    desksByType: DesksByType
    hallTables: Table[]
    seededFromFullHall: boolean
    goodRoadCount: number
    pinnedTableIds: number[]
}

export enum GoodRoadMutation {
    SET_HALL_TABLES = 'SET_GOOD_ROAD_HALL_TABLES',
    SET_DESKS_BY_TYPE = 'SET_GOOD_ROAD_DESKS_BY_TYPE',
    UPSERT_TABLE = 'UPSERT_GOOD_ROAD_TABLE',
    TOGGLE_PINNED_TABLE = 'TOGGLE_GOOD_ROAD_PINNED_TABLE',
    CLEAR = 'CLEAR_GOOD_ROAD'
}

const emptyDesksByType = (): DesksByType => ({
    G002: [],
    G003: []
})

function collectGoodRoadTableIds(desksByType: DesksByType): number[] {
    return [
        ...(desksByType.G002 || []).map(table => table.id),
        ...(desksByType.G003 || []).map(table => table.id)
    ]
}

function replaceTypeTables(
    desksByType: DesksByType,
    gameTypeNo: 'G002' | 'G003',
    tables: Table[]
): DesksByType {
    return {
        ...desksByType,
        [gameTypeNo]: tables.filter(table => table.gameTypeNo === gameTypeNo)
    }
}

function mergeTable(current: Table, update: Table): Table {
    return {
        ...current,
        ...update,
        expand: {
            ...current.expand,
            ...update.expand
        }
    }
}

function upsertIntoHallTables(
    hallTables: Table[],
    table: Table
): Table[] {
    const list = [...hallTables]
    const index = list.findIndex(item => item.id === table.id)
    if (index !== -1) {
        list.splice(index, 1, mergeTable(list[index], table))
    } else {
        list.push(table)
    }
    return list
}

function refreshGoodRoadCount(moduleState: GoodRoadState): void {
    moduleState.goodRoadCount = countGoodRoadTables([
        ...(moduleState.desksByType.G002 || []),
        ...(moduleState.desksByType.G003 || [])
    ])
}

function persistGoodRoadState(moduleState: GoodRoadState): void {
    setCookieValue(
        Token.HALL_TABLES,
        JSON.stringify(moduleState.hallTables || []).b64encode()
    )
    setCookieValue(
        Token.GOOD_ROAD_DESKS,
        JSON.stringify(moduleState.desksByType || emptyDesksByType()).b64encode()
    )
}

function clearPersistedGoodRoadState(): void {
    setCookieValue(Token.HALL_TABLES, JSON.stringify([]).b64encode())
    setCookieValue(
        Token.GOOD_ROAD_DESKS,
        JSON.stringify(emptyDesksByType()).b64encode()
    )
}

function readPersistedJson<T>(key: string, fallback: T): T {
    const raw = getCookieValue(key)
    if (!raw) return fallback

    try {
        return raw.b64decode().toObject<T>()
    } catch {
        try {
            return raw.toObject<T>()
        } catch {
            setCookieValue(key, JSON.stringify(fallback).b64encode())
            return fallback
        }
    }
}

function loadPersistedHallTables(): Table[] {
    const tables = readPersistedJson<Table[]>(Token.HALL_TABLES, [])
    return Array.isArray(tables) ? tables : []
}

function loadPersistedDesksByType(): DesksByType {
    const desks = readPersistedJson<DesksByType>(
        Token.GOOD_ROAD_DESKS,
        emptyDesksByType()
    )
    return {
        G002: Array.isArray(desks?.G002) ? desks.G002 : [],
        G003: Array.isArray(desks?.G003) ? desks.G003 : []
    }
}

const persistedHallTables = loadPersistedHallTables()
const persistedDesksByType = loadPersistedDesksByType()
const hasPersistedHallData = persistedHallTables.length > 0

const state: GoodRoadState = {
    desksByType: persistedDesksByType,
    hallTables: persistedHallTables,
    seededFromFullHall: hasPersistedHallData,
    goodRoadCount: countGoodRoadTables([
        ...(persistedDesksByType.G002 || []),
        ...(persistedDesksByType.G003 || [])
    ]),
    pinnedTableIds: []
}

const mutations: MutationTree<GoodRoadState> = {
    [GoodRoadMutation.SET_HALL_TABLES](
        moduleState,
        payload: { tables: Table[]; gameTypeNo?: string }
    ) {
        const tables = payload.tables || []
        const gameTypeNo = payload.gameTypeNo || ''

        if (!gameTypeNo) {
            moduleState.hallTables = tables.map(table => ({ ...table }))
            moduleState.desksByType = splitGoodRoadTablesByType(
                filterGoodRoadGameTables(tables)
            ) as DesksByType
            moduleState.seededFromFullHall = true
            refreshGoodRoadCount(moduleState)
            persistGoodRoadState(moduleState)
            return
        }

        if (gameTypeNo === 'G002' || gameTypeNo === 'G003') {
            moduleState.desksByType = replaceTypeTables(
                moduleState.desksByType,
                gameTypeNo,
                tables
            )
            tables.forEach(table => {
                if (table.gameTypeNo === gameTypeNo) {
                    moduleState.hallTables = upsertIntoHallTables(
                        moduleState.hallTables,
                        table
                    )
                }
            })
            refreshGoodRoadCount(moduleState)
            persistGoodRoadState(moduleState)
        }
    },
    [GoodRoadMutation.SET_DESKS_BY_TYPE](
        moduleState,
        desksByType: DesksByType
    ) {
        moduleState.desksByType = {
            G002: [...(desksByType.G002 || [])],
            G003: [...(desksByType.G003 || [])]
        }
        ;[...(desksByType.G002 || []), ...(desksByType.G003 || [])].forEach(
            table => {
                moduleState.hallTables = upsertIntoHallTables(
                    moduleState.hallTables,
                    table
                )
            }
        )
        moduleState.seededFromFullHall = true
        refreshGoodRoadCount(moduleState)
        persistGoodRoadState(moduleState)
    },
    [GoodRoadMutation.UPSERT_TABLE](moduleState, table: Table) {
        if (!table) return

        moduleState.hallTables = upsertIntoHallTables(
            moduleState.hallTables,
            table
        )

        if (!isGoodRoadGameType(table.gameTypeNo)) {
            return
        }

        const gameTypeNo = table.gameTypeNo as 'G002' | 'G003'
        const list = [...(moduleState.desksByType[gameTypeNo] || [])]
        const index = list.findIndex(item => item.id === table.id)

        if (index !== -1) {
            list.splice(index, 1, mergeTable(list[index], table))
        } else {
            list.push(table)
        }

        moduleState.desksByType = {
            ...moduleState.desksByType,
            [gameTypeNo]: list
        }
        refreshGoodRoadCount(moduleState)
    },
    [GoodRoadMutation.TOGGLE_PINNED_TABLE](moduleState, tableId: number) {
        const ids = moduleState.pinnedTableIds || []
        if (ids.includes(tableId)) {
            moduleState.pinnedTableIds = ids.filter(id => id !== tableId)
            return
        }
        moduleState.pinnedTableIds = [tableId, ...ids]
    },
    [GoodRoadMutation.CLEAR](moduleState) {
        moduleState.desksByType = emptyDesksByType()
        moduleState.hallTables = []
        moduleState.seededFromFullHall = false
        moduleState.goodRoadCount = 0
        moduleState.pinnedTableIds = []
        clearPersistedGoodRoadState()
    }
}

const actions: ActionTree<GoodRoadState, State> = {
    setGoodRoadHallTables(
        { commit },
        payload: { tables: Table[]; gameTypeNo?: string }
    ): void {
        commit(GoodRoadMutation.SET_HALL_TABLES, payload)
    },
    setGoodRoadDesksByType({ commit }, desksByType: DesksByType): void {
        commit(GoodRoadMutation.SET_DESKS_BY_TYPE, desksByType)
    },
    upsertGoodRoadTable({ commit }, table: Table): void {
        commit(GoodRoadMutation.UPSERT_TABLE, table)
    },
    clearGoodRoad({ commit }): void {
        commit(GoodRoadMutation.CLEAR)
    }
}

const getters: GetterTree<GoodRoadState, State> = {
    goodRoadDesksByType(moduleState): DesksByType {
        return moduleState.desksByType
    },
    goodRoadTables(moduleState): Table[] {
        return [
            ...(moduleState.desksByType.G002 || []),
            ...(moduleState.desksByType.G003 || [])
        ]
    },
    hallTables(moduleState): Table[] {
        return moduleState.hallTables
    },
    goodRoadCount(moduleState): number {
        return moduleState.goodRoadCount
    },
    goodRoadTableIds(moduleState): number[] {
        return collectGoodRoadTableIds(moduleState.desksByType)
    },
    hasGoodRoadHallData(moduleState): boolean {
        return moduleState.seededFromFullHall
    },
    pinnedTableIds(moduleState): number[] {
        return moduleState.pinnedTableIds || []
    }
}

export const goodRoad: Module<GoodRoadState, State> = {
    state,
    mutations,
    actions,
    getters
}
